import normalize from 'json-api-normalizer';
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import {
  JSONApiDataObject,
  JSONApiDataObjectLoadable,
  ProfileReview,
  ReduxEntity,
  ReviewModel
} from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { sellerEndpoint } from 'Douleutaras/redux/data/seller/sellerProfilesSlice';
import { usersSlice } from 'Douleutaras/redux/data/users/usersSlice';
import { RootState } from 'Douleutaras/redux/store';
import { sellerReviewsResponseSlice } from 'Douleutaras/redux/data/seller/sellerReviewsResponseSlice';

export const singleSellerReviewsEndpoint = userName => `${sellerEndpoint}${userName}/reviews/`;

const sellerReviewsAdapter = createEntityAdapter<JSONApiDataObjectLoadable<ReviewModel>>({
  selectId: item => item.id
});

export const getSellerReviews = createAsyncThunk(
  'seller/review/get',
  async ({ username, page, isPinned }: { username: string; page: number; isPinned?: boolean }, api) => {
    const additionalGetParams: Record<string, string | number> = {
      'page[number]': page
    };

    if (isPinned !== undefined) {
      additionalGetParams['filter[is_pinned]'] = String(isPinned);
    }

    const response = await makeRequest(singleSellerReviewsEndpoint(username), {
      method: 'GET',
      // base include it for all requests
      includes: ['buyer', 'response', 'client'],
      additionalGetParams
    })(api);

    const result = reviewsHandleResponse(response, api, singleSellerReviewsEndpoint(username));

    return {
      ...result,
      isPinned,
      pinnedId: isPinned && response.data.data.length > 0 ? response.data.data[0].id : undefined
    };
  }
);

const reviewsHandleResponse = (response, api, endpoint) => {
  const normalizedReview = normalize(response.data, { endpoint });
  const { usersAdded } = usersSlice.actions;
  const { sellerReviewsResponseAdded } = sellerReviewsResponseSlice.actions;

  if (normalizedReview.users) {
    api.dispatch(
      usersAdded(normalizedReview.users)
    );
  }
  if (normalizedReview.externalClient) {
    api.dispatch(
      usersAdded(normalizedReview.externalClient)
    );
  }
  if (normalizedReview.feedbackResponse) {
    api.dispatch(
      sellerReviewsResponseAdded(normalizedReview.feedbackResponse)
    );
  }

  normalizedReview.endpoint = endpoint;

  return normalizedReview;
};

export const sellerReviewsSlice = createSlice({
  name: 'sellerReviews',
  initialState: sellerReviewsAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    pinnedItemId: undefined as string | undefined,
    entities: {}
  } as ReduxEntity<JSONApiDataObject<ProfileReview>> & { pinnedItemId: string | undefined }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getSellerReviews.fulfilled, (state, action) => {
      if (action.payload.isPinned) {
        state.pinnedItemId = action.payload.pinnedId;
        if (action.payload.feedback) {
          sellerReviewsAdapter.setMany(state, action.payload.feedback);
        }

        return;
      }

      if (action.payload.feedback) {
        sellerReviewsAdapter.upsertMany(state, action.payload.feedback);
      }
      state.meta[action.payload.endpoint] = action.payload.meta[action.payload.endpoint];

      state.loading = false;
      state.error = false;
    });
  }
});

export const sellerReviewsSelectors = sellerReviewsAdapter.getSelectors((state: RootState) => state.sellerReviews);

export const selectPinnedSellerReview = (state: RootState) => {
  const pinnedId = state.sellerReviews.pinnedItemId;

  if (!pinnedId) {
    return undefined;
  }

  return state.sellerReviews.entities[pinnedId];
};

export default sellerReviewsSlice.reducer;
